import React, { useEffect, useState } from 'react';
import Button from '../../common/Button';
import { MemberDTO } from '../../../services/apiClient';
import FormField from '../../common/FormField';
import handleChange from '../../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../../hooks/useFormValidation';
import MapboxAutocomplete from '../../common/formElements/MapboxAutocomplete';
import { CountryConstant } from '../../../constants/CountryConstants';
import { ProvinceConstant } from '../../../constants/ProvinceConstants';
import { provinceLogic } from '../../../utils/provinceLogic';

interface Props {
  member: MemberDTO;
  onChange: (updated: Partial<MemberDTO>) => void;
  onNext: () => void;
  onBack: () => void;
}

interface SameAs {
  sameAs: boolean;
}

const AddressDetails: React.FC<Props> = ({
  member,
  onChange,
  onNext,
  onBack,
}) => {
  const [formData, setFormData] = useState<MemberDTO>(member);
  const [sameAs, setSameAs] = useState<SameAs>({ sameAs: true });

  const {
    provinceOptions,
    isLoadingProvinces,
    shouldShowProvinceOther,
    findProvinceByName,
    setProvinceOptionsToNotOnList,
  } = provinceLogic({
    countryId: CountryConstant.SOUTH_AFRICA,
    provinceId: formData.provinceId,
    provinceOther: formData.provinceOther,
    autoSetNotOnList: false, // Disable auto-setting - use manual matching instead
    onProvinceChange: (newProvinceId) => {
      const updates = {
        provinceId: newProvinceId,
        provinceOther:
          newProvinceId === ProvinceConstant.NOT_ON_LIST
            ? formData.provinceOther
            : undefined,
      };

      setFormData((prev) => ({
        ...prev,
        ...updates,
      }));

      onChange(updates);
    },
  });
  const validationRules: ValidationRules = {
    provinceId: [{ required: true, message: 'Province is required' }],
    physicalAddress: [{ required: true, message: 'Street Name is required' }],
    physicalSuburb: [{ required: true, message: 'Suburb Name is required' }],
    physicalTown: [
      { required: true, message: 'Closest City/Town is required' },
    ],
    physicalPostalCode: [
      { required: true, message: 'Postal Code is required' },
      {
        custom: (value) => {
          if (!value) return true;
          return validateSAPostalCode(String(value));
        },
        message: 'Postal Code is not valid',
      },
    ],
    courierAddress: [
      { required: !sameAs.sameAs, message: 'Delivery Street Name is required' },
    ],
    courierSuburb: [
      { required: !sameAs.sameAs, message: 'Delivery Suburb Name is required' },
    ],
    courierTown: [
      {
        required: !sameAs.sameAs,
        message: 'Delivery Closest City/Town is required',
      },
    ],
    courierPostalCode: [
      { required: !sameAs.sameAs, message: 'Delivery Postal Code is required' },
      {
        custom: (value) => {
          if (!value) return true;
          return validateSAPostalCode(String(value));
        },
        message: 'Delivery Postal Code is not valid',
      },
    ],
    provinceOther: [
      {
        required: shouldShowProvinceOther,
        message: 'Province Other is required',
      },
    ],
  };

  const { errors, validate } = useFormValidation<MemberDTO>(validationRules);

  const handleNext = () => {
    if (validate(formData)) {
      // Create submission data with provinceId set to undefined if it's "Not on List"
      const submissionData: Partial<MemberDTO> = {};

      if (sameAs.sameAs) {
        submissionData.courierAddress = formData.physicalAddress;
        submissionData.courierSuburb = formData.physicalSuburb;
        submissionData.courierTown = formData.physicalTown;
        submissionData.courierPostalCode = formData.physicalPostalCode;
      }

      // Set provinceId to undefined if it's "Not on List"
      if (formData.provinceId === ProvinceConstant.NOT_ON_LIST) {
        submissionData.provinceId = undefined;
      }

      onChange(submissionData);
      onNext();
    }
  };

  const validateSAPostalCode = (postalCode: string): boolean => {
    if (!postalCode) return true; // let "required" handle empty
    const cleaned = String(postalCode).trim().replace(/\s+/g, '');
    return /^\d{4}$/.test(cleaned);
  };

  useEffect(() => {
    if (!sameAs.sameAs) {
      setFormData((prev) => ({
        ...prev,
        courierAddress: '',
        courierSuburb: '',
        courierTown: '',
        courierPostalCode: '',
      }));
    }
  }, [sameAs.sameAs]);

  const handleLocationSelect = (latitude: number, longitude: number, addressComponents?: any) => {
    const locationProvince = addressComponents?.region || '';

    const matchedProvinceId = findProvinceByName(locationProvince);

    const addressParts = [
      addressComponents?.addressLine1,
      addressComponents?.addressLine2,
      addressComponents?.town,
      addressComponents?.postalCode,
    ]
      .filter(Boolean)
      .join(', ');

    const addressData = {
      physicalAddress: addressParts,
      physicalSuburb: addressComponents?.addressLine2 || '',
      physicalTown: addressComponents?.town || '',
      physicalPostalCode: addressComponents?.postalCode || '',
    };

    if (matchedProvinceId) {
      // Province found - set it directly
      const updates = {
        ...addressData,
        provinceId: matchedProvinceId,
        provinceOther: undefined,
      };

      setFormData((prev) => ({
        ...prev,
        ...updates,
      }));

      onChange(updates);
    } else {
      // No province match - set to "Not on List" and require manual input
      const updates = {
        ...addressData,
        provinceId: ProvinceConstant.NOT_ON_LIST,
        provinceOther: locationProvince || '',
      };

      setFormData((prev) => ({
        ...prev,
        ...updates,
      }));

      onChange(updates);
    }
  };

  const handleCourierLocationSelect = (latitude: number, longitude: number, addressComponents?: any) => {
    const addressParts = [
      addressComponents?.addressLine1,
      addressComponents?.addressLine2,
      addressComponents?.town,
      addressComponents?.postalCode,
    ]
      .filter(Boolean)
      .join(', ');

    const courierData = {
      courierAddress: addressParts,
      courierSuburb: addressComponents?.addressLine2 || '',
      courierTown: addressComponents?.town || '',
      courierPostalCode: addressComponents?.postalCode || '',
    };

    setFormData((prev) => ({
      ...prev,
      ...courierData,
    }));

    onChange(courierData);
  };

  return (
    <div>
      <MapboxAutocomplete
        fieldId="physicalAddress"
        fieldLabel="Street Name (Current Residence)"
        value={formData.physicalAddress || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        onLocationSelect={handleLocationSelect}
        placeholder="e.g 15 Joe Street"
        required={true}
        error={errors.physicalAddress}
      />

      <FormField
        id="provinceId"
        label="Province"
        type="dropdown"
        value={formData.provinceId || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        placeholder="Select Province"
        required={true}
        error={errors.provinceId}
        options={provinceOptions}
        isLoading={isLoadingProvinces}
      />

      {shouldShowProvinceOther && (
        <FormField
          id="provinceOther"
          label="Province Other"
          type="text"
          value={formData.provinceOther || ''}
          onChange={(e) => handleChange(e, setFormData, onChange)}
          placeholder="Enter Province Name"
          required={shouldShowProvinceOther}
          error={errors.provinceOther}
        />
      )}

      <FormField
        id="physicalSuburb"
        label="Suburb Name"
        type="text"
        value={formData.physicalSuburb || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        placeholder="e.g Greenstone Hill"
        required={true}
        error={errors.physicalSuburb}
      />

      <FormField
        id="physicalTown"
        label="Closest City/Town"
        type="text"
        value={formData.physicalTown || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        placeholder="e.g Johannesburg"
        required={true}
        error={errors.physicalTown}
      />

      <FormField
        id="physicalPostalCode"
        label="Postal Code"
        type="text"
        value={formData.physicalPostalCode || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        placeholder="e.g 1609"
        required={true}
        error={errors.physicalPostalCode}
      />

      <FormField
        id="sameAs"
        label="Delivery Address"
        type="checkbox"
        value={sameAs.sameAs}
        onChange={(e) => handleChange(e, setSameAs)}
        helper="Same as physical address"
      />

      {!sameAs.sameAs && (
        <>
          <MapboxAutocomplete
            fieldId="courierAddress"
            fieldLabel="Delivery Street Name"
            value={formData.courierAddress || ''}
            onChange={(e) => handleChange(e, setFormData, onChange)}
            onLocationSelect={handleCourierLocationSelect}
            placeholder="e.g Joe Street"
            required={!sameAs.sameAs}
            error={errors.courierAddress}
          />
          <FormField
            id="courierSuburb"
            label="Delivery Suburb Name"
            type="text"
            value={formData.courierSuburb || ''}
            onChange={(e) => handleChange(e, setFormData, onChange)}
            placeholder="e.g Greenstone Hill"
            required={!sameAs.sameAs}
            error={errors.courierSuburb}
          />

          <FormField
            id="courierTown"
            label="Delivery Closest City/Town"
            type="text"
            value={formData.courierTown || ''}
            onChange={(e) => handleChange(e, setFormData, onChange)}
            placeholder="e.g Johannesburg"
            required={!sameAs.sameAs}
            error={errors.courierTown}
          />

          <FormField
            id="courierPostalCode"
            label="Delivery Postal Code"
            type="text"
            value={formData.courierPostalCode || ''}
            onChange={(e) => handleChange(e, setFormData, onChange)}
            placeholder="e.g 1609"
            required={!sameAs.sameAs}
            error={errors.courierPostalCode}
          />
        </>
      )}

      <div className="mt-4 flex justify-between w-full">
        <Button onClick={onBack} variant="secondary">
          Back
        </Button>
        <Button onClick={handleNext} variant="primary">
          Next
        </Button>
      </div>
    </div>
  );
};

export default AddressDetails;
